import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { Box, Flex, Text } from '@updater/ui-uds';
import {
  CompletedSegment,
  ValidatingSegment,
} from 'flows/core/components/cards/progress-bar-card/progress-bar-styles';
import { fontFamilyFromPath, sanitizeFontPath } from 'utils/font';

const HeroSection = styled(Box)`
  background: ${({ theme }) => theme.whiteLabelTheme?.colors.heroBackground};
  overflow: hidden;
  position: relative;
`;

const HeroInner = styled(Box)`
  position: relative;
  padding: 24px 20px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  box-sizing: border-box;

  ${({ theme }) => theme.mediaQueries.m} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px;
    min-height: 200px;
  }

  ${({ theme }) => theme.mediaQueries.l} {
    padding-left: ${({ theme }) => theme.space.m}px;
    padding-right: ${({ theme }) => theme.space.m}px;
    max-width: calc(720px + ${({ theme }) => theme.space.xxl}px);
  }
`;

const HeroTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
  ${({ theme }) => {
    const normalFont = theme.whiteLabelTheme?.fonts?.normal;
    if (!normalFont || !sanitizeFontPath(normalFont)) return '';
    const family = fontFamilyFromPath(normalFont, 'WhiteLabelFont');
    return `font-family: '${family}', ${theme.fonts.body};`;
  }}
`;

const ChecklistText = styled.em`
  color: ${({ theme }) => theme.whiteLabelTheme?.colors.heroText};
  font-style: italic;
`;

const HeroSubtext = styled(Text)`
  color: ${({ theme }) => theme.whiteLabelTheme?.colors.heroSubtext};
`;

const ProgressSection = styled(Box)`
  margin-top: 24px;
`;

const ProgressHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ProgressLabel = styled(Text)`
  color: ${({ theme }) => theme.whiteLabelTheme?.colors.progressBarText};
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const ProgressCount = styled(Text)`
  color: ${({ theme }) => theme.whiteLabelTheme?.colors.progressBarText};
`;

const CompletedCount = styled(Text)`
  color: ${({ theme }) => theme.colors.white};
`;

const ProgressBarContainer = styled(Box)`
  height: 8px;
  background: ${({ theme }) => theme.whiteLabelTheme?.colors.progressBar};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

interface WhiteLabelHeroProps {
  firstName?: string | null;
  totalTasks?: number;
  completedTasks?: number;
  validatingTasks?: number;
}

export const WhiteLabelHero: React.FC<WhiteLabelHeroProps> = ({
  firstName,
  totalTasks = 0,
  completedTasks = 0,
  validatingTasks = 0,
}) => {
  const completedPct =
    totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const validatingPct =
    totalTasks > 0 ? Math.round((validatingTasks / totalTasks) * 100) : 0;
  const displayedDone = completedTasks + validatingTasks;
  const portalTarget = document.getElementById('white-label-hero-slot');

  const hero = (
    <HeroSection height={['100%']} as="section">
      <HeroInner>
        <HeroTitle
          variant={['xlBold', 'xlBold', 'xxlBold', 'xxlBold', 'xxlBold']}
          mb="s"
          as="h1"
        >
          Your move-in <ChecklistText>checklist</ChecklistText>, {firstName}.
        </HeroTitle>
        <HeroSubtext variant={['s', 's', 'm']}>
          {totalTasks} things before and after move-in day.
        </HeroSubtext>

        <ProgressSection>
          <ProgressHeader>
            <ProgressLabel variant="xsBold">PROGRESS</ProgressLabel>
            <ProgressCount variant="m">
              <CompletedCount variant="lBold" as="span">
                {displayedDone}
              </CompletedCount>{' '}
              /{totalTasks} done
            </ProgressCount>
          </ProgressHeader>
          <ProgressBarContainer>
            <Flex width="100%" height="100%">
              {completedPct > 0 && (
                <CompletedSegment
                  widthPct={completedPct}
                  hasValidating={validatingPct > 0}
                  data-tooltip="Completed tasks"
                />
              )}
              {validatingPct > 0 && (
                <ValidatingSegment
                  widthPct={validatingPct}
                  hasCompleted={completedPct > 0}
                  data-tooltip="Tasks in review"
                />
              )}
            </Flex>
          </ProgressBarContainer>
        </ProgressSection>
      </HeroInner>
    </HeroSection>
  );

  if (portalTarget) {
    return createPortal(hero, portalTarget);
  }

  return hero;
};
